const FILL_BOTTOM_OFFSET = 4;
const FILL_COLOR_OFFSET = 6;
const MARKER_SHAPE_OFFSET = 3;
const MARKER_COLOR_OFFSET = 5;

/**
 * Background segments between two lines (value i to i + 1), for
 * fillRenderer; prices relative to `priceBase`. A segment without all four
 * values stays transparent.
 *
 * @param {object} params
 * @param {{ between: string[], shift: number, color: number[] }} params.plot
 * @param {Object<string, number[]>} params.series
 * @param {number} params.first - index of the first value of the first segment
 * @param {number} params.count - segments
 * @param {number} params.priceBase
 * @param {number} params.floatsPerInstance - fillRenderer.floatsPerInstance
 * @returns {Float32Array}
 */
export const buildFillSegments = ({ plot, series, first, count, priceBase, floatsPerInstance }) => {
  const data = new Float32Array(Math.max(0, count) * floatsPerInstance);
  const [upper, lower] = plot.between.map((key) => series[key]);

  for (let position = 0; position < count; position += 1) {
    const index = first + position;
    const values = [upper[index], upper[index + 1], lower[index], lower[index + 1]];
    const offset = position * floatsPerInstance;

    data[offset] = index + plot.shift;
    data[offset + 2] = index + 1 + plot.shift;

    if (values.every(Number.isFinite)) {
      data[offset + 1] = values[0] - priceBase;
      data[offset + 3] = values[1] - priceBase;
      data[offset + FILL_BOTTOM_OFFSET] = values[2] - priceBase;
      data[offset + FILL_BOTTOM_OFFSET + 1] = values[3] - priceBase;
      data.set(plot.color, offset + FILL_COLOR_OFFSET);
    }
  }

  return data;
};

/**
 * One marker per value (a dot or a triangle), for markerRenderer; a bar
 * without a value gets size 0 and is not drawn.
 *
 * @param {object} params
 * @param {{ key: string, shift: number, shape: number, size: number, offset: number, color: number[] }} params.plot
 * @param {Object<string, number[]>} params.series
 * @param {number} params.first
 * @param {number} params.count
 * @param {number} params.priceBase
 * @param {number} params.floatsPerInstance - markerRenderer.floatsPerInstance
 * @returns {Float32Array}
 */
export const buildMarkers = ({ plot, series, first, count, priceBase, floatsPerInstance }) => {
  const data = new Float32Array(Math.max(0, count) * floatsPerInstance);
  const values = series[plot.key];

  for (let position = 0; position < count; position += 1) {
    const index = first + position;
    const value = values[index];
    const offset = position * floatsPerInstance;

    data[offset] = index + plot.shift;

    if (Number.isFinite(value)) {
      data[offset + 1] = value - priceBase;
      data[offset + 2] = plot.offset;
      data[offset + MARKER_SHAPE_OFFSET] = plot.shape;
      data[offset + MARKER_SHAPE_OFFSET + 1] = plot.size;
      data.set(plot.color, offset + MARKER_COLOR_OFFSET);
    }
  }

  return data;
};
